// Lógica compartilhada pra manter o cadastro de Clientes/Veículos em dia a partir de uma tela de documento
// (Documentos Complementares e futuras declarações): comparar os dados preenchidos na tela com o que já está
// no cadastro, localizar um cliente existente por CPF/CNPJ antes de criar um novo (evita duplicidade) e
// gravar só o que foi preenchido, sem apagar nada do cadastro.
import axios from 'axios';
import config from '../config/config';

const { API_BASE_URL } = config;

function apenasDigitos(v) {
  return (v || '').toString().replace(/\D/g, '');
}

function authHeader() {
  const token = localStorage.getItem('jwtToken');
  return { headers: { Authorization: `Bearer ${token}` } };
}

// Normaliza para comparar com o cadastro: CPF/CNPJ só dígitos; UF, placa e chassi em maiúsculas; sem espaços nas pontas
export function normalizar(campo, valor) {
  const v = (valor ?? '').toString().trim();
  if (campo === 'cpfCnpj') return apenasDigitos(v);
  if (campo === 'placa') return v.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (['uf', 'chassi'].includes(campo)) return v.toUpperCase();
  return v;
}

// Campos preenchidos na tela que são novos ou diferentes do cadastro. Campo em branco não conta (não apaga nada).
export function camposDiferentes(atual, origem, campos) {
  return campos.filter((c) => {
    const novo = normalizar(c, atual[c]);
    return novo !== '' && novo !== normalizar(c, origem ? origem[c] : '');
  });
}

// Situação dos dados em relação ao cadastro: 'vazio' | 'novo' | 'alterado' | 'igual' | 'desconhecido'
export function situacaoCadastro({ vinculado, origem, atual, campos, obrigatorios, rotuloNovo, camposFixos = [], rotulosCampo = {} }) {
  if (!vinculado) {
    const completo = obrigatorios.every((c) => normalizar(c, atual[c]) !== '');
    return completo ? { tipo: 'novo', texto: rotuloNovo } : { tipo: 'vazio', texto: 'Preencha os dados para poder salvá-los no cadastro.' };
  }
  if (!origem) return { tipo: 'desconhecido', texto: 'Atualiza o cadastro com os dados preenchidos aqui (campos em branco não apagam nada).' };
  const diferentes = camposDiferentes(atual, origem, campos.filter((c) => !camposFixos.includes(c)));
  if (diferentes.length === 0) return { tipo: 'igual', texto: 'Dados iguais aos do cadastro.' };
  return { tipo: 'alterado', texto: `Novo ou diferente do cadastro: ${diferentes.map((c) => rotulosCampo[c] || c).join(', ')}.` };
}

// Cliente do cadastro com este CPF/CNPJ (ou null). No Nexus o CPF e o CNPJ têm endpoints separados.
export async function buscarClientePorDocumento(documento) {
  const d = apenasDigitos(documento);
  const tipo = d.length === 11 ? 'cpf' : d.length === 14 ? 'cnpj' : null;
  if (!tipo) return null;
  try {
    const response = await axios.get(`${API_BASE_URL}/clientes/${tipo}/${d}`, authHeader());
    return response.data && response.data.idCliente ? response.data : null;
  } catch (err) {
    return null;
  }
}

// Campos da pessoa do formulário → campos do cliente no Nexus (só os preenchidos)
function camposPreenchidosDoCliente(pessoa) {
  const doc = apenasDigitos(pessoa.cpfCnpj);
  const campos = {
    nome: pessoa.nome,
    identidade: pessoa.rg,
    telefone: apenasDigitos(pessoa.telefone),
    email: pessoa.email,
    cep: apenasDigitos(pessoa.cep),
    logradouro: pessoa.logradouro,
    numero: pessoa.numero,
    complemento: pessoa.complemento,
    bairro: pessoa.bairro,
    cidade: pessoa.cidade,
    uf: (pessoa.uf || '').toUpperCase(),
  };
  if (doc.length === 11) campos.cpf = doc;
  if (doc.length === 14) campos.cnpj = doc;
  return Object.fromEntries(Object.entries(campos)
    .map(([k, v]) => [k, (v ?? '').toString().trim()])
    .filter(([, v]) => v !== ''));
}

// Atualiza um cliente existente só com os campos preenchidos. O PUT do Nexus grava o cliente inteiro,
// então lê o cadastro completo antes e só troca o que veio preenchido: nenhum outro campo (inclusive os
// usados pela NFS-e, como código IBGE e inscrição municipal) é perdido.
export async function complementarCliente(idCliente, pessoa) {
  const { data: atual } = await axios.get(`${API_BASE_URL}/clientes/${idCliente}`, authHeader());
  const { data } = await axios.put(`${API_BASE_URL}/clientes/${idCliente}`, { ...atual, ...camposPreenchidosDoCliente(pessoa) }, authHeader());
  return data || { ...atual, ...camposPreenchidosDoCliente(pessoa) };
}

// Cria um cliente novo com os campos preenchidos
export async function criarCliente(pessoa) {
  const { data } = await axios.post(`${API_BASE_URL}/clientes`, { ...camposPreenchidosDoCliente(pessoa), ativo: true }, authHeader());
  return data;
}
